import React from 'react';
import DefaultLoadingScreen from 'components/Loading/DefaultLoadingScreen';
import useInviter, { LOAD_INVITER_ERROR_TYPE } from 'data/inviter/useInviter';
import { useMasthead } from 'data/masthead/useMasthead';
import { useUserData } from 'data/user/useUserData';
import { Locale } from 'lib/i18n';
import { useRouter } from 'next/router';

import GeneralError from 'containers/Error/GeneralError';
import InvitationExpiredError from 'containers/Error/InvitationExpiredError';
import PlanAlreadyFullError from 'containers/Error/PlanAlreadyFullError';
import { useError } from 'data/error/useError';
import { isErrorOfTypeAndCode } from 'data/error/utils';
import { PlanType } from 'lib/plans';
import { DuoSubLandingPageContainer } from './Duo';
import { FamilySubLandingPageContainer } from './Family';
import { getDefaultTermsUrl } from 'lib/url';

export function SubLandingPageContainer({ planType }: { planType: PlanType }) {
  const router = useRouter();
  const mastheadProps = useMasthead(router.locale as Locale);
  const { error, errors } = useError();
  const { userData } = useUserData();

  const { inviteToken, isLoadingInviter, inviterData } = useInviter();

  const inviterError = errors.find(
    e => e?.type === LOAD_INVITER_ERROR_TYPE && e?.status !== 401,
  );

  const shouldDisplayGeneralError = typeof inviterError !== 'undefined';

  if (error) {
    if (isErrorOfTypeAndCode(error, LOAD_INVITER_ERROR_TYPE, 'PLAN_IS_FULL')) {
      return <PlanAlreadyFullError error={error} />;
    }

    if (
      isErrorOfTypeAndCode(error, LOAD_INVITER_ERROR_TYPE, 'INVITE_NOT_FOUND')
    )
      return <InvitationExpiredError error={error} />;
  }

  if (shouldDisplayGeneralError) {
    return <GeneralError error={inviterError} />;
  }

  if (isLoadingInviter || mastheadProps.isLoading) {
    return <DefaultLoadingScreen darkMode={false} />;
  }

  const isAnonymous = !userData;

  return (
    <>
      {planType === 'family' ? (
        <FamilySubLandingPageContainer
          legalTermsUrl={inviterData?.legalTermsUrl ?? getDefaultTermsUrl()}
          mastheadData={mastheadProps.data}
          authenticated={!isAnonymous}
          inviteToken={inviteToken!}
        />
      ) : (
        <DuoSubLandingPageContainer
          legalTermsUrl={inviterData?.legalTermsUrl ?? getDefaultTermsUrl()}
          mastheadData={mastheadProps.data}
          authenticated={!isAnonymous}
          inviteToken={inviteToken!}
        />
      )}
    </>
  );
}
